import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAgentDataStore} from './agent-data.store';
import AgentDateRangeFilter, {useAgentDateRange} from './AgentDateRangeFilter';
import AgentDataTable from './AgentDataTable';
import stickySurfaceStyles from './refer-earn-sticky-surface.module.scss';
import {useStickyActive} from './use-sticky-active';

const columns = ['userId', 'team', 'superiorId', 'createTime'] as const;
const tableGridClassName = 'grid-cols-[1fr_0.75fr_1.3fr_1.7fr]';

const SubordinateDataContent = () => {
  const {t} = useTranslation();
  const [keyword, setKeyword] = useState('');
  const {range, dateRangeValue, setDateRangeValue} = useAgentDateRange();
  const subordinateList = useAgentDataStore(state => state.subordinateList);
  const subordinateLoading = useAgentDataStore(
    state => state.subordinateLoading,
  );
  const loadSubordinateData = useAgentDataStore(
    state => state.loadSubordinateData,
  );
  const {active: stickyActive, sentinelRef} = useStickyActive();
  const rows = subordinateList?.content || [];

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadSubordinateData({
        ...range,
        userId: keyword ? Number(keyword) || undefined : undefined,
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, loadSubordinateData, range]);

  return (
    <section className="px-0 pb-4">
      <span ref={sentinelRef} aria-hidden="true" className="block h-px" />
      <div
        className={[
          stickyActive ? stickySurfaceStyles.stickySurface : '',
          stickyActive ? stickySurfaceStyles.dataControlsSurface : '',
          'sticky top-[3.0625rem] z-20 -mx-2 grid grid-cols-[minmax(0,180fr)_minmax(0,160fr)] gap-[0.625rem] bg-transparent px-2 py-[0.875rem]',
        ].join(' ')}>
        <AgentDateRangeFilter
          className="w-full"
          range={range}
          value={dateRangeValue}
          onChange={setDateRangeValue}
        />
        <label className="relative flex h-12 min-w-0 items-center gap-[0.1875rem] rounded-lg bg-[rgba(255,255,255,0.72)] px-2 shadow-[inset_0_0_0_0.0625rem_rgba(255,255,255,0.45)] backdrop-blur-[0.125rem]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1.25rem"
            height="1.25rem"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
            className="shrink-0">
            <path
              d="M12.7338 13.5204L11.9389 12.7246C10.9954 13.4977 9.78999 13.962 8.4762 13.9635C5.45197 13.9635 3 11.5091 3 8.4818C3 5.45446 5.45197 3 8.4762 3C11.5004 3 13.9524 5.45446 13.9524 8.4818C13.9508 9.78738 13.4933 10.986 12.7317 11.9274L13.5282 12.7246L13.5102 12.7421L13.5171 12.7363C13.689 12.5997 13.9397 12.6114 14.0989 12.7702L16.8719 15.546C17.0427 15.7171 17.0427 15.995 16.8719 16.1665L16.1674 16.8718C15.9965 17.0427 15.7189 17.0427 15.5476 16.8718L12.7745 14.0959C12.6158 13.9371 12.6042 13.6861 12.7407 13.5135L12.7338 13.5204ZM8.4762 4.31247C6.17602 4.31247 4.31113 6.17926 4.31113 8.4818C4.31113 10.7843 6.17602 12.6511 8.4762 12.6511C10.7764 12.6511 12.6412 10.7843 12.6412 8.4818C12.6391 6.17979 10.7758 4.31459 8.4762 4.31247Z"
              fill="black"
            />
          </svg>
          <input
            type="search"
            aria-label={t('invitation.subordinateData.searchPlaceholder')}
            placeholder={t('invitation.subordinateData.searchPlaceholder')}
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[0.75rem] font-normal leading-none text-black outline-none placeholder:text-black"
          />
        </label>
      </div>

      <AgentDataTable
        loading={subordinateLoading}
        empty={!rows.length}
        emptyText={t('invitation.agentData.noData')}
        gridClassName={tableGridClassName}
        columns={columns.map(column =>
          t(`invitation.subordinateData.columns.${column}`),
        )}>
        {rows.map((row, index) => (
          <div
            key={`${row.userId}-${row.registerTime}-${index}`}
            className={[
              'grid h-10 items-center px-3 text-[0.75rem] font-normal leading-[1.144rem] text-black',
              tableGridClassName,
              index % 2 === 1 ? 'bg-[rgba(140,140,140,0.06)]' : '',
            ].join(' ')}>
            <div className="truncate text-center">{row.userId}</div>
            <div className="truncate text-center">{row.teamLevel}</div>
            <div className="truncate text-center">{row.parentAgentUserId}</div>
            <div className="truncate text-center">{row.registerTime}</div>
          </div>
        ))}
      </AgentDataTable>
    </section>
  );
};

export default SubordinateDataContent;
